import * as z from 'zod';

export const googleChronicleDynamicValidations = (data, ctx) => {
  if (data.auth_method === 'service_account_dict') {
    const requiredFields = ['service_account_dict', 'private_key'];

    requiredFields.forEach(field => {
      if (!data[field]) {
        ctx.issues.push({
          path: [`google_chronicle.${field}`],
          code: 'custom',
          message: `${field.replace(/_/g, ' ')} is required when auth method is service_account_dict`,
          input: ''
        });
      }
    });
  } else if (data.auth_method === 'user_credentials') {
    const requiredFields = ['access_token'];

    requiredFields.forEach(field => {
      if (!data[field]) {
        ctx.issues.push({
          path: [`google_chronicle.${field}`],
          code: 'custom',
          message: `${field.replace(/_/g, ' ')} is required when auth method is user_credentials`,
          input: ''
        });
      }
    });
  } else if (data.auth_method === 'user_credentials_refresh_token') {
    const requiredFields = ['access_token', 'refresh_token', 'client_id', 'client_secret'];

    requiredFields.forEach(field => {
      if (!data[field]) {
        ctx.issues.push({
          path: [`google_chronicle.${field}`],
          code: 'custom',
          message: `${field.replace(/_/g, ' ')} is required when auth method is user_credentials_refresh_token`,
          input: ''
        });
      }
    });

    if (!data.token_uri || !z.string().url().safeParse(data.token_uri).success) {
      ctx.issues.push({
        path: ['google_chronicle.token_uri'],
        code: 'custom',
        message: 'Token URI must be a valid URL',
        input: ''
      });
    }
  }
};

export const googleChroniclePartition = z.object({
  url: z.string().max(150).url().nonempty(),
  auth_method: z.enum(['service_account_dict', 'user_credentials', 'user_credentials_refresh_token']),
  detection_offset: z.coerce.number().min(0).max(2000000),
  use_curated_rules: z.boolean(),
  use_custom_rules: z.boolean().optional(),
  generate_alert_as_events: z.boolean().optional(),
  verify_ssl: z.boolean(),
  request_timeout: z.coerce.number().min(0).max(2000000),
  use_asset_filtering: z.boolean().optional(),
  store_event_raw: z.boolean(),

  // IF auth_method === 'key'
  service_account_dict: z.string().max(5500).optional(),
  private_key: z.string().max(5500).optional(),

  // IF auth_method === 'user_credentials' || 'user_credentials_refresh_token'
  access_token: z.string().max(600).optional(),

  // IF auth_method === 'user_credentials_refresh_token'
  refresh_token: z.string().max(600).optional(),
  token_uri: z.string().max(100).optional(),
  client_id: z.string().max(255).optional(),
  client_secret: z.string().max(255).optional()
});

export const gChronicleAttackIqPartition = z.object({
  // Hidden For Now
  // debug: z.boolean(),
  // log_max_mb: z.coerce.number().min(0).max(2000000),
  // log_backup_count: z.coerce.number().min(0).max(2000000),
  // connector_expiration_time: z.coerce.number().optional(),
  // connector_expiration_unit: z.enum(timeUnits).optional(),
  // source_type_period_update_value: z.coerce.number().min(0).max(9999),
  // source_type_period_update_unit: z.enum(timeUnits),
  // event_interval_value: z.coerce.number().min(0).max(9999),
  // event_interval_unit: z.enum(timeUnits),
  // technology_alert_interval_value: z.coerce.number().min(0).max(9999),
  // technology_alert_interval_unit: z.enum(timeUnits),

  use_security_events: z.enum(['custom_rules', 'all', 'security_events'])
});

export const gChronicleGeneralPartition = z.object({
  display_name: z.string().max(50).nonempty('This field is required'),
  description: z.string().max(200).optional(),

  mode: z.enum(['Automatic', 'Ad Hoc'])
});

export const googleChronicleFormSchema = z
  .object({
    general: gChronicleGeneralPartition.required(),
    // GC
    google_chronicle: googleChroniclePartition,
    attack_iq: gChronicleAttackIqPartition.required()
  })
  .superRefine((data, ctx) => googleChronicleDynamicValidations(data.google_chronicle, ctx));

export type GoogleChronicleConfig = z.infer<typeof googleChronicleFormSchema>;
